/**
 * Строка техкарты — заведение и правка (макет v2-05, таблица «Операции варианта»).
 *
 * Форма повторяет то, что агроном пишет в бумажной техкарте: что делаем, когда
 * (агроокно), сколько раз (кратность), на каком объёме, своими силами или
 * наймом, сколько сцепок ставим и с какой нормой выработки, сколько горючего.
 * Материалы правятся отдельной кнопкой строки — их набор живёт своей таблицей.
 *
 * Тип работы (`company_op_type_id`) задаётся только при заведении: к нему
 * привязаны нормы выработки и расценки (089), и смена типа у существующей
 * строки означала бы другую работу — проще завести новую. API патч типа тоже
 * не принимает.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { matchesSearch } from '@/lib/reference/normalizeSearch';
import { useBudgetSection, type BudgetSection } from '@/lib/planning/budgetCatalog';
import { useCompanyOpTypes } from '@/lib/reference/companyOperationTypes';
import {
  BLOCK_ORDER,
  BLOCK_TITLES,
  EXECUTION_TITLES,
  type BlockKind,
  type OperationInput,
  type OperationPatch,
  type TechCardOperation,
  type TechCardStage,
} from '@/lib/planning/techCards';
import { cn } from '@/lib/utils';

export interface OperationDialogSubmit {
  create?: OperationInput;
  patch?: OperationPatch;
}

/** Пустая строка ввода → null, «1,5» → 1.5, мусор → undefined (не трогаем поле). */
function parseNumber(raw: string): number | null | undefined {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return value;
}

/**
 * База нормы расхода в форме — только та, что строка умеет хранить.
 * Унаследованное «л/100 км» (нормативы хозяйства, AK-731) переводим в «л/км»:
 * та же величина, другая запись.
 */
export function normalizeFuelForForm(
  rate: number | null | undefined,
  basis: string | null | undefined,
): { rate: string; basis: string } {
  if (basis === 'per_100km') {
    return { rate: rate == null ? '' : String(rate / 100), basis: 'per_km' };
  }
  return { rate: rate == null ? '' : String(rate), basis: basis ?? 'per_ha' };
}

export function OperationDialog({
  operation,
  blockKind,
  stages = [],
  defaultStageId = null,
  parentCandidates,
  seasonId = null,
  saving,
  onSubmit,
  onClose,
}: {
  /** `null` — заводим новую строку. */
  operation: TechCardOperation | null;
  blockKind: BlockKind;
  /** Этапы карты (ADR-071 D3) — своя группировка агронома, не block_kind. */
  stages?: TechCardStage[];
  defaultStageId?: string | null;
  /** Основные операции карты — кандидаты в «родители» вспомогательной работы. */
  parentCandidates: TechCardOperation[];
  /** Сезон карты — из его бюджетного справочника берутся ГСМ и наёмные работы. */
  seasonId?: string | null;
  saving: boolean;
  onSubmit: (values: OperationDialogSubmit) => void;
  onClose: () => void;
}) {
  const isNew = operation === null;
  const [opTypeId, setOpTypeId] = useState<string | null>(operation?.company_op_type_id ?? null);
  const [block, setBlock] = useState<BlockKind>(
    (operation?.block_kind as BlockKind | undefined) ?? blockKind,
  );
  const [stageId, setStageId] = useState<string | null>(
    operation ? (operation.stage_id ?? null) : defaultStageId,
  );
  const [areaShare, setAreaShare] = useState(
    operation?.area_share_pct === null || operation?.area_share_pct === undefined
      ? '100'
      : String(operation.area_share_pct),
  );
  const [name, setName] = useState(operation?.name ?? '');
  const [parentId, setParentId] = useState<string | null>(operation?.parent_operation_id ?? null);
  const [from, setFrom] = useState(operation?.window_starts_on ?? '');
  const [to, setTo] = useState(operation?.window_ends_on ?? '');
  const [workDays, setWorkDays] = useState(
    operation?.window_work_days === null || operation?.window_work_days === undefined
      ? ''
      : String(operation.window_work_days),
  );
  const [multiplicity, setMultiplicity] = useState(String(operation?.multiplicity ?? 1));
  const [volumeBasis, setVolumeBasis] = useState<TechCardOperation['volume_basis']>(
    operation?.volume_basis ?? 'per_ha',
  );
  const [volume, setVolume] = useState(
    operation?.volume === null || operation?.volume === undefined ? '' : String(operation.volume),
  );
  const [execution, setExecution] = useState<TechCardOperation['execution_kind']>(
    operation?.execution_kind ?? 'own',
  );
  const [machineryCount, setMachineryCount] = useState(
    operation?.machinery_count === null || operation?.machinery_count === undefined
      ? ''
      : String(operation.machinery_count),
  );
  const [throughput, setThroughput] = useState(
    operation?.throughput_norm === null || operation?.throughput_norm === undefined
      ? ''
      : String(operation.throughput_norm),
  );
  // Норма расхода могла приехать из нормативов хозяйства как «л/100 км»
  // (AK-731): строка так хранить не умеет (CHECK ck_tco_fuel_basis знает только
  // per_km), поэтому в форме показываем её уже в «л/км» — иначе «Сохранить»
  // без единой правки отвечало бы 422.
  const initialFuel = normalizeFuelForForm(operation?.fuel_rate, operation?.fuel_rate_basis);
  const [fuelRate, setFuelRate] = useState(initialFuel.rate);
  // ГСМ (ADR-071 D5-D6): вид топлива из бюджетного справочника, база нормы,
  // коэффициент накладных (только к «на га»), свой/чужой, плечо и рейс.
  const [fuelItemId, setFuelItemId] = useState<string | null>(operation?.fuel_item_id ?? null);
  const [fuelBasis, setFuelBasis] = useState<string>(initialFuel.basis);
  const [fuelCoef, setFuelCoef] = useState(
    operation?.fuel_overhead_coef == null || operation.fuel_overhead_coef === 1
      ? ''
      : String(operation.fuel_overhead_coef),
  );
  const [fuelOwnership, setFuelOwnership] = useState<'auto' | 'own' | 'provider'>(
    operation?.fuel_ownership ?? 'auto',
  );
  const [haulKm, setHaulKm] = useState(
    operation?.haul_distance_km == null ? '' : String(operation.haul_distance_km),
  );
  const [haulCap, setHaulCap] = useState(
    operation?.haul_capacity_t == null ? '' : String(operation.haul_capacity_t),
  );
  // Наёмная услуга (ADR-071 D6-d п.9): позиция аутсорса, база и СВОЯ доля.
  const [outsourceItemId, setOutsourceItemId] = useState<string | null>(
    operation?.outsource_item_id ?? null,
  );
  const [outsourceBasis, setOutsourceBasis] = useState<'per_unit' | 'per_ton_km'>(
    operation?.outsource_rate_basis ?? 'per_unit',
  );
  const [outsourceShare, setOutsourceShare] = useState(
    operation?.outsource_share_pct == null || operation.outsource_share_pct === 100
      ? ''
      : String(operation.outsource_share_pct),
  );
  // НЗП (ADR-070): по умолчанию отнесение выводится из календаря (год начала
  // агроокна раньше года сезона карты), ручное значение — редкое осознанное
  // действие, и оно видно в таблице пометкой «вручную».
  const [wipMode, setWipMode] = useState<'auto' | 'yes' | 'no'>(
    operation?.wip_override === true ? 'yes' : operation?.wip_override === false ? 'no' : 'auto',
  );
  const [error, setError] = useState<string | null>(null);

  const windowBroken = !!from && !!to && from > to;

  function submit() {
    if (isNew && !opTypeId) {
      setError('Выберите операцию — к ней привязаны нормы выработки и расценки.');
      return;
    }
    if (windowBroken) {
      setError('Начало агроокна позже его конца — проверьте даты.');
      return;
    }
    const numbers = {
      window_work_days: parseNumber(workDays),
      multiplicity: parseNumber(multiplicity),
      volume: parseNumber(volume),
      machinery_count: parseNumber(machineryCount),
      throughput_norm: parseNumber(throughput),
      fuel_rate: parseNumber(fuelRate),
      area_share_pct: parseNumber(areaShare),
      fuel_overhead_coef: parseNumber(fuelCoef),
      haul_distance_km: parseNumber(haulKm),
      haul_capacity_t: parseNumber(haulCap),
      outsource_share_pct: parseNumber(outsourceShare),
    };
    if (Object.values(numbers).some((v) => v === undefined)) {
      setError('Числовые поля принимают только неотрицательные числа.');
      return;
    }
    if (!numbers.multiplicity) {
      setError('Кратность — сколько раз операцию делают за сезон; ноля здесь не бывает.');
      return;
    }
    const sharePct = numbers.area_share_pct ?? 100;
    if (sharePct <= 0 || sharePct > 100) {
      setError('Доля площади — процент от 0 (не включая) до 100.');
      return;
    }
    const outsourceSharePct = numbers.outsource_share_pct ?? 100;
    if (outsourceSharePct <= 0 || outsourceSharePct > 100) {
      setError('Доля наёмной услуги — процент от 0 (не включая) до 100.');
      return;
    }
    if (numbers.fuel_overhead_coef === 0) {
      setError('Коэффициент накладных на топливо не бывает нулём — пусто значит 1.');
      return;
    }
    if (numbers.haul_capacity_t === 0) {
      setError('Грузоподъёмность рейса не бывает нулём — оставьте пустым, если не про перевозку.');
      return;
    }
    const common = {
      block_kind: block,
      stage_id: stageId,
      name: name.trim() || null,
      parent_operation_id: parentId,
      window_starts_on: from || null,
      window_ends_on: to || null,
      window_work_days: numbers.window_work_days,
      multiplicity: numbers.multiplicity,
      volume_basis: volumeBasis,
      volume: numbers.volume,
      area_share_pct: sharePct,
      execution_kind: execution,
      machinery_count: numbers.machinery_count,
      throughput_norm: numbers.throughput_norm,
      fuel_item_id: fuelItemId,
      fuel_rate: numbers.fuel_rate,
      fuel_rate_basis: fuelBasis,
      fuel_ownership: fuelOwnership === 'auto' ? null : fuelOwnership,
      fuel_overhead_coef: numbers.fuel_overhead_coef ?? 1,
      haul_distance_km: numbers.haul_distance_km,
      haul_capacity_t: numbers.haul_capacity_t,
      outsource_item_id: outsourceItemId,
      outsource_rate_basis: outsourceBasis,
      outsource_share_pct: outsourceSharePct,
      wip_override: wipMode === 'auto' ? null : wipMode === 'yes',
    };
    setError(null);
    if (isNew) {
      onSubmit({ create: { company_op_type_id: opTypeId as string, ...common } });
    } else {
      onSubmit({ patch: common });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isNew ? 'Новая операция техкарты' : `Операция: ${operation?.op_type_name ?? 'без типа'}`}
          </DialogTitle>
          <DialogDescription>
            Агроокно принадлежит этой техкарте: два варианта одной культуры могут сеять в
            разные сроки. Материалы правятся отдельно — кнопкой «Материалы» в строке.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isNew && (
            <div>
              <Label>Название операции</Label>
              <OpTypeSelect value={opTypeId} onChange={setOpTypeId} />
              <p className="mt-1 text-xs text-muted-foreground">
                Список — названия операций вашего хозяйства. Нет нужной — заведите её в
                справочнике операций.
              </p>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {stages.length > 0 && (
              <div>
                <Label htmlFor="op-stage">Этап карты</Label>
                <select
                  id="op-stage"
                  value={stageId ?? ''}
                  onChange={(e) => setStageId(e.target.value || null)}
                  className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground"
                >
                  <option value="">без этапа</option>
                  {[...stages]
                    .sort((a, b) => a.seq - b.seq)
                    .map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                </select>
                <p className="mt-1 text-xs text-muted-foreground">
                  Этапы — ваша группировка работ в этой карте; добавить и переименовать их
                  можно над таблицей.
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="op-name">Уточнение к названию</Label>
              <Input
                id="op-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="например «Подкормка №1»"
              />
            </div>
            <div className={stages.length > 0 ? 'sm:col-span-2' : ''}>
              <Label htmlFor="op-block">Блок работ (для «Заменить блок»)</Label>
              <div id="op-block" className="mt-1 flex flex-wrap gap-2">
                {BLOCK_ORDER.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    aria-pressed={kind === block}
                    onClick={() => setBlock(kind)}
                    className={
                      kind === block
                        ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                        : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                    }
                  >
                    {BLOCK_TITLES[kind]}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                По блоку работает замена набора операций из другого варианта — он не заменяет
                ваши этапы.
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="op-from">Агроокно с</Label>
              <Input
                id="op-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="op-to">по</Label>
              <Input id="op-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="op-days">Рабочих дней в окне</Label>
              <Input
                id="op-days"
                inputMode="numeric"
                value={workDays}
                onChange={(e) => setWorkDays(e.target.value)}
                placeholder="например 14"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="op-wip">Отнесение к НЗП</Label>
            <div id="op-wip" className="mt-1 flex flex-wrap gap-2">
              {(
                [
                  ['auto', 'По календарю (авто)'],
                  ['yes', 'НЗП'],
                  ['no', 'Не НЗП'],
                ] as const
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={mode === wipMode}
                  onClick={() => setWipMode(mode)}
                  className={
                    mode === wipMode
                      ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                      : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                  }
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              НЗП — осенние работы под этот урожай, выполненные в прошлом сезоне. По календарю
              работа считается НЗП, когда её агроокно начинается раньше года уборки; ручное
              отнесение нужно только для исключений (пар, переносы окна).
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="op-mult">Кратность за сезон</Label>
              <Input
                id="op-mult"
                inputMode="decimal"
                value={multiplicity}
                onChange={(e) => setMultiplicity(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="op-share">Доля площади, %</Label>
              <Input
                id="op-share"
                inputMode="decimal"
                value={areaShare}
                onChange={(e) => setAreaShare(e.target.value)}
                placeholder="100"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Краевая обработка или подкормка части клина — меньше 100.
              </p>
            </div>
            <div>
              <Label htmlFor="op-volume">Объём работ</Label>
              <Input
                id="op-volume"
                inputMode="decimal"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                placeholder="пусто — вся площадь"
              />
            </div>
            <div>
              <Label htmlFor="op-basis">Считаем на</Label>
              <div id="op-basis" className="mt-1 flex flex-wrap gap-2">
                {(
                  [
                    ['per_ha', 'гектары'],
                    ['per_ton', 'тонны'],
                    ['fixed', 'разы'],
                  ] as const
                ).map(([value, title]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={volumeBasis === value}
                    onClick={() => setVolumeBasis(value)}
                    className={
                      volumeBasis === value
                        ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                        : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                    }
                  >
                    {title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="op-exec">Кто выполняет</Label>
              <div id="op-exec" className="mt-1 flex flex-wrap gap-2">
                {(Object.keys(EXECUTION_TITLES) as TechCardOperation['execution_kind'][]).map(
                  (kind) => (
                    <button
                      key={kind}
                      type="button"
                      aria-pressed={execution === kind}
                      onClick={() => setExecution(kind)}
                      className={
                        execution === kind
                          ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
                          : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                      }
                    >
                      {EXECUTION_TITLES[kind]}
                    </button>
                  ),
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="op-units">Сколько сцепок ставим</Label>
              <Input
                id="op-units"
                inputMode="numeric"
                value={machineryCount}
                onChange={(e) => setMachineryCount(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="op-norm">Норма выработки</Label>
              <Input
                id="op-norm"
                inputMode="decimal"
                value={throughput}
                onChange={(e) => setThroughput(e.target.value)}
                placeholder="по нормативу"
              />
            </div>
          </div>

          <div className="rounded-md border border-border p-3">
            <div className="text-sm font-semibold text-foreground">Топливо</div>
            <div className="mt-2 grid gap-4 sm:grid-cols-3">
              <div>
                <Label>Вид топлива</Label>
                <BudgetItemSelect
                  seasonId={seasonId}
                  section="fuel"
                  value={fuelItemId}
                  onChange={setFuelItemId}
                  placeholder="из справочника ГСМ…"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Цена берётся из бюджетного справочника сезона. Без вида топлива расход не
                  попадёт в деньги.
                </p>
              </div>
              <div>
                <Label htmlFor="op-fuel">Норма расхода, л</Label>
                <Input
                  id="op-fuel"
                  inputMode="decimal"
                  value={fuelRate}
                  onChange={(e) => setFuelRate(e.target.value)}
                />
                <div className="mt-1 flex flex-wrap gap-1">
                  {(
                    [
                      ['per_ha', 'на га'],
                      ['per_hour', 'в час'],
                      ['per_shift', 'за смену'],
                      ['per_km', 'на км'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={fuelBasis === value}
                      onClick={() => setFuelBasis(value)}
                      className={
                        fuelBasis === value
                          ? 'rounded-full border border-primary bg-primary/10 px-2 py-1 text-xs font-medium text-primary transition-colors'
                          : 'rounded-full border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="op-fuel-coef">Коэффициент накладных</Label>
                <Input
                  id="op-fuel-coef"
                  inputMode="decimal"
                  value={fuelCoef}
                  onChange={(e) => setFuelCoef(e.target.value)}
                  placeholder="1 — без надбавки"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Перегоны и развороты (у полевых работ обычно 1,1). Действует только на
                  норму «на га».
                </p>
              </div>
              <div>
                <Label>Чьё топливо</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {(
                    [
                      ['auto', 'как исполнитель'],
                      ['own', 'наше'],
                      ['provider', 'подрядчика'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={fuelOwnership === value}
                      onClick={() => setFuelOwnership(value)}
                      className={
                        fuelOwnership === value
                          ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-medium text-primary transition-colors'
                          : 'rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  «Наняли технику, солярка наша» — выберите «наше» при наёмной работе.
                </p>
              </div>
              <div>
                <Label htmlFor="op-haul-km">Плечо перевозки, км</Label>
                <Input
                  id="op-haul-km"
                  inputMode="decimal"
                  value={haulKm}
                  onChange={(e) => setHaulKm(e.target.value)}
                  placeholder="из карты или хозяйства"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Пусто — берётся плечо карты, затем среднее расстояние хозяйства.
                </p>
              </div>
              <div>
                <Label htmlFor="op-haul-cap">Грузоподъёмность рейса, т</Label>
                <Input
                  id="op-haul-cap"
                  inputMode="decimal"
                  value={haulCap}
                  onChange={(e) => setHaulCap(e.target.value)}
                  placeholder="для нормы «на км»"
                />
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border p-3">
            <div className="text-sm font-semibold text-foreground">Наёмная работа</div>
            <div className="mt-2 grid gap-4 sm:grid-cols-3">
              <div>
                <Label>Позиция из справочника</Label>
                <BudgetItemSelect
                  seasonId={seasonId}
                  section="outsource"
                  value={outsourceItemId}
                  onChange={setOutsourceItemId}
                  placeholder="из справочника наёмных работ…"
                />
              </div>
              <div>
                <Label>Расценка за</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {(
                    [
                      ['per_unit', 'единицу объёма'],
                      ['per_ton_km', 'тонно-километр'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={outsourceBasis === value}
                      onClick={() => setOutsourceBasis(value)}
                      className={
                        outsourceBasis === value
                          ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-medium text-primary transition-colors'
                          : 'rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {outsourceBasis === 'per_ton_km' && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Считается от объёма в тоннах и плеча перевозки (поле выше).
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="op-out-share">Доля услуги, %</Label>
                <Input
                  id="op-out-share"
                  inputMode="decimal"
                  value={outsourceShare}
                  onChange={(e) => setOutsourceShare(e.target.value)}
                  placeholder="100"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Своя доля: наняли вывоз на 75 % урожая — поставьте 75. Не зависит от доли
                  площади операции.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="op-parent">Вспомогательная работа при</Label>
              <select
                id="op-parent"
                value={parentId ?? ''}
                onChange={(e) => setParentId(e.target.value || null)}
                className="mt-1 h-10 w-full rounded-md border border-border bg-background px-3 text-sm text-foreground"
              >
                <option value="">самостоятельная операция</option>
                {parentCandidates
                  .filter((op) => op.id !== operation?.id)
                  .map((op) => (
                    <option key={op.id} value={op.id}>
                      {op.name ?? op.op_type_name ?? 'операция без названия'}
                    </option>
                  ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Подвоз воды, семян, загрузка — идут свитой под основной операцией.
              </p>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={saving}>
            {isNew ? 'Добавить операцию' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Выбор позиции бюджетного справочника (ГСМ, наёмные работы): алфавит + поиск
 * (сквозное правило выпадашек). «Не задано» — явный пункт: убрать топливо со
 * строки — легальное действие, а не ошибка.
 */
function BudgetItemSelect({
  seasonId,
  section,
  value,
  onChange,
  placeholder,
}: {
  seasonId: string | null;
  section: BudgetSection;
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const items = useBudgetSection({ seasonId, section });

  const options = useMemo(() => {
    const active = (items.data ?? []).filter((it) => !it.archived_at);
    const sorted = [...active].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    return sorted.filter((it) => matchesSearch(it.name, term));
  }, [items.data, term]);

  const selected = (items.data ?? []).find((it) => it.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="mt-1 w-full justify-between font-normal">
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? selected.name : value ? 'позиция убрана из бюджета' : placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="relative border-b border-border p-2">
          <Search className="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Поиск позиции…"
            className="h-8 pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm text-muted-foreground hover:bg-accent"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
          >
            <Check className={cn('h-4 w-4', value === null ? 'opacity-100' : 'opacity-0')} />
            не задано
          </button>
          {items.isLoading && <p className="p-2 text-sm text-muted-foreground">Загружаем…</p>}
          {!items.isLoading && options.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">
              В бюджетном справочнике сезона нет таких позиций — заведите её там.
            </p>
          )}
          {options.map((item) => (
            <button
              key={item.id}
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
              onClick={() => {
                onChange(item.id);
                setOpen(false);
              }}
            >
              <Check className={cn('h-4 w-4', item.id === value ? 'opacity-100' : 'opacity-0')} />
              <span className="truncate">{item.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Выбор операции хозяйства: алфавит + поиск (сквозное правило выпадашек). */
function OpTypeSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const types = useCompanyOpTypes();

  const options = useMemo(() => {
    const all = [...(types.data ?? [])].sort((a, b) => a.local_name.localeCompare(b.local_name, 'ru'));
    return all.filter((t) => matchesSearch(t.local_name, term));
  }, [types.data, term]);

  const selected = types.data?.find((t) => t.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal">
          <span className={cn(!selected && 'text-muted-foreground')}>
            {selected ? selected.local_name : 'Выберите операцию…'}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="relative border-b border-border p-2">
          <Search className="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Поиск операции…"
            className="h-8 pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {types.isLoading && <p className="p-2 text-sm text-muted-foreground">Загружаем…</p>}
          {!types.isLoading && options.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">Ничего не нашлось</p>
          )}
          {options.map((type) => (
            <button
              key={type.id}
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
              onClick={() => {
                onChange(type.id);
                setOpen(false);
              }}
            >
              <Check className={cn('h-4 w-4', type.id === value ? 'opacity-100' : 'opacity-0')} />
              {type.local_name}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
